import styled from '@emotion/styled'

import { type ArcadeIcon, NavigationBackIcon, NavigationCloseIcon } from '@arcade/web/icons/react'
import { ArcadeButton, Text } from '@cash-design-system/react'

const Header = styled.div`
  position: absolute;
  top: calc(env(safe-area-inset-top) + ${({ theme }) => theme.spacing.m});
  right: calc(env(safe-area-inset-right) + ${({ theme }) => theme.spacing.m});
  left: calc(env(safe-area-inset-left) + ${({ theme }) => theme.spacing.m});
  z-index: 4;
  display: flex;
  align-items: center;
  pointer-events: none;

  ${({ theme }) => theme.breakpoints.tablet} {
    top: calc(env(safe-area-inset-top) + ${({ theme }) => theme.spacing.defaultContent});
    right: calc(env(safe-area-inset-right) + ${({ theme }) => theme.spacing.defaultContent});
    left: calc(env(safe-area-inset-left) + ${({ theme }) => theme.spacing.defaultContent});
  }
`

const HeaderButton = styled(ArcadeButton)`
  pointer-events: auto;
  opacity: 0.7;
  transition: opacity 150ms ease;

  > span {
    width: ${({ theme }) => theme.spacing.xxl};
    height: ${({ theme }) => theme.spacing.xxl};
    min-height: ${({ theme }) => theme.spacing.xxl};
    border-radius: ${({ theme }) => theme.border.radius.circle};
    box-shadow:
      ${({ theme }) => theme.dropShadows.dialog},
      inset 0 0 0 ${({ theme }) => theme.spacing.xxl} rgb(0 0 0 / 4%);

    ${({ theme }) => theme.breakpoints.tablet} {
      width: 4.5rem;
      height: 4.5rem;
      min-height: 4.5rem;
      box-shadow:
        ${({ theme }) => theme.dropShadows.dialog},
        inset 0 0 0 4.5rem rgb(0 0 0 / 4%);
    }
  }

  &:hover,
  &:focus-visible {
    opacity: 0.9;
  }
`

const HeaderTitle = styled(Text)<{ $hideTitle?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: auto;

  ${({ $hideTitle, theme }) =>
    $hideTitle &&
    `${theme.breakpoints.tablet} {
      display: none;
    }`}
`

const ICONS = {
  back: NavigationBackIcon,
  close: NavigationCloseIcon,
}

export type FloatingNavigationHeaderAction = {
  ariaLabel: string
  disabled?: boolean
  icon: keyof typeof ICONS
  onClick: () => void
}

export type FloatingNavigationHeaderProps = {
  hideTitle?: boolean
  leadingAction?: FloatingNavigationHeaderAction
  title?: string
}

const ActionButton = ({ action }: { action: FloatingNavigationHeaderAction }) => {
  const Icon = ICONS[action.icon]

  return (
    <HeaderButton
      ariaLabel={action.ariaLabel}
      disabled={action.disabled}
      Icon={Icon as ArcadeIcon}
      onClick={action.onClick}
      prominence="standard"
      size="icon"
    />
  )
}

export const FloatingNavigationHeader = ({ hideTitle, leadingAction, title }: FloatingNavigationHeaderProps) => (
  <Header>
    {leadingAction ? <ActionButton action={leadingAction} /> : null}
    {title ? (
      <HeaderTitle $hideTitle={hideTitle} as="h1" color="textStandard" typography="titleBarPageTitle">
        {title}
      </HeaderTitle>
    ) : null}
  </Header>
)
